/**
 * Configurable pricing configuration - single source of truth
 * Easy to update pricing without touching multiple files
 *
 * TO UPDATE PRICING:
 * 1. Change the monthly prices in PRICING_CONFIG below
 * 2. Update Stripe price IDs in the STRIPE_PRICE_IDS section
 * 3. Modify features in the respective feature arrays
 * 4. Annual pricing is automatically calculated with the discount
 */

/**
 * Feature type with availability status
 */
export type Feature = {
	text: string
	available: boolean
	comingSoon?: boolean
}

/**
 * 🎯 MAIN PRICING CONFIGURATION
 * Update these values to change pricing across the entire app
 */
export const PRICING_CONFIG = {
	ANNUAL_DISCOUNT_PERCENT: 30,

	// 💰 BASE MONTHLY PRICING (in USD) - Change these to update pricing
	FREE: {
		monthly: 0,
		annual: 0,
		features: [
			{ text: '1 Profile', available: true },
			{ text: '5 Tasks per profile', available: true },
			{ text: 'Profile-based blocking', available: true },
			{ text: 'Task-based blocking', available: true },
			{ text: 'Basic support', available: true },
		] as Feature[],
	},
	PRO: {
		monthly: 15,
		get annual() {
			return Math.round(
				this.monthly * 12 * (1 - PRICING_CONFIG.ANNUAL_DISCOUNT_PERCENT / 100),
			)
		},
		features: [
			{ text: 'Unlimited Profiles', available: true },
			{ text: 'Unlimited Tasks', available: true },
			{ text: 'Profile-based blocking', available: true },
			{ text: 'Task-based blocking', available: true },
			{ text: 'Priority support', available: true },
			{ text: 'Tasks sync', available: false, comingSoon: true },
		] as Feature[],
	},
} as const

/**
 * 🔗 STRIPE PRICE IDS
 * Separate development and production price IDs
 */
const DEV_PRICE_IDS = {
	PRO: {
		monthly: 'price_1SQ4Iu2cLEejmc4ND6DzVO3x',
		annual: 'price_1SQ4J82cLEejmc4NVIsuAxKP',
	},
} as const

const PROD_PRICE_IDS = {
	PRO: {
		monthly: 'price_1SQ4QSK3ohFNPHEtOj4E4IVV',
		annual: 'price_1SQ4QSK3ohFNPHEti7LGhZ1S',
	},
} as const

/**
 * Active Stripe price IDs based on environment
 */
export const STRIPE_PRICE_IDS =
	process.env.NODE_ENV === 'production' ? PROD_PRICE_IDS : DEV_PRICE_IDS

/**
 * Pricing plans for UI components
 */
export const PRICING_PLANS = [
	{
		id: 'free',
		name: 'Free',
		description: 'Perfect to get started',
		monthlyPrice: PRICING_CONFIG.FREE.monthly,
		annualPrice: PRICING_CONFIG.FREE.annual,
		features: PRICING_CONFIG.FREE.features,
		priceIds: null, // Free plan has no price IDs
		isPrimary: false,
		badge: undefined,
		ctaText: {
			monthly: 'Get Started',
			annual: 'Get Started',
		},
		isContactSales: false,
	},
	{
		id: 'pro',
		name: 'Pro',
		description: 'For power users',
		monthlyPrice: PRICING_CONFIG.PRO.monthly,
		annualPrice: PRICING_CONFIG.PRO.annual,
		features: PRICING_CONFIG.PRO.features,
		priceIds: {
			monthly: STRIPE_PRICE_IDS.PRO.monthly,
			annual: STRIPE_PRICE_IDS.PRO.annual,
		},
		isPrimary: true,
		badge: 'Most Popular',
		ctaText: {
			monthly: 'Upgrade to Pro',
			annual: 'Upgrade to Pro',
		},
		isContactSales: false,
	},
] as const

/**
 * Helper functions for pricing calculations
 */
export const PRICING_UTILS = {
	getAnnualSavings: (monthlyPrice: number) => {
		const annualPrice = Math.round(
			monthlyPrice * 12 * (1 - PRICING_CONFIG.ANNUAL_DISCOUNT_PERCENT / 100),
		)
		const monthlyCost = monthlyPrice * 12
		return monthlyCost - annualPrice
	},

	getFormattedPrice: (
		price: number,
		period: 'monthly' | 'annual' = 'monthly',
	) => {
		const periodText = period === 'monthly' ? '/month' : '/year'
		return `$${price}${periodText}`
	},

	getFormattedSavings: (monthlyPrice: number) => {
		const savings = PRICING_UTILS.getAnnualSavings(monthlyPrice)
		return `Save $${savings}/year`
	},
} as const
